import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { fetchSchoolOverview } from '../api/dashboard'
import Header from '../components/Header'
import HistoryGraphModal from '../components/HistoryGraphModal'
import Sidebar from '../components/Sidebar'
import SpeedtestModalWebSocket from '../components/SpeedtestModalWebSocket'
import { DevicesTable } from '../components/dashboard'
import RegionsStats from '../components/dashboard/RegionsStats'
import { useDashboardData } from '../hooks/useDashboardData'

const getDeviceIdentifier = (device) =>
  device?.ip_address || device?.ip || device?.ipAddress || ''

const buildDeviceChartData = (series, t) => {
  if (!series?.in?.length || !series?.out?.length) {
    return null
  }

  return {
    labels: series.in.map((point) =>
      new Date(point[0] * 1000).toLocaleString('ru-KZ', {
        timeZone: 'Asia/Almaty',
        hour: '2-digit',
        minute: '2-digit',
      })
    ),
    datasets: [
      {
        label: t('dashboard.incomingTraffic'),
        data: series.in.map((point) => parseFloat(point[1])),
        borderColor: 'rgb(42, 209, 0)',
        backgroundColor: 'rgba(42, 209, 0, 0.2)',
        fill: false,
        tension: 0.4,
      },
      {
        label: t('dashboard.outgoingTraffic'),
        data: series.out.map((point) => parseFloat(point[1])),
        borderColor: 'rgb(255, 173, 8)',
        backgroundColor: 'rgba(255, 173, 8, 0.2)',
        fill: false,
        tension: 0.4,
      },
    ],
  }
}

const RegionsStatsPage = ({ setIsAuthenticated, onLogout, role, userRole, userEmail }) => {
  const { t } = useTranslation();
  const [isSidebarVisible, setSidebarVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  })
  const [selectedRegion, setSelectedRegion] = useState('')
  const [logsTimeRange, setLogsTimeRange] = useState('24h')
  const [selectedDevice, setSelectedDevice] = useState('')
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false)
  const [deviceChartData, setDeviceChartData] = useState(null)
  const [logsStatus, setLogsStatus] = useState('')
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsReloadKey, setLogsReloadKey] = useState(0)
  const [selectedSpeedtestDevice, setSelectedSpeedtestDevice] = useState(null)
  const navigate = useNavigate()
  const { regionsStats, devices, summaryLoading, fetchDeviceSeries } = useDashboardData()

  const toggleSidebar = () => setSidebarVisible((prev) => !prev)

  const handleLogout = () => {
    if (typeof onLogout === 'function') {
      onLogout()
      return
    }

    localStorage.removeItem('authToken')
    setIsAuthenticated(false)
  }

  const handleRegionClick = (region) => {
    setSelectedRegion(region)
    // Прокручиваем к таблице
    setTimeout(() => {
      const tableElement = document.getElementById('devices-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleOpenLogs = (deviceIp) => {
    setSelectedDevice(deviceIp)
    setDeviceChartData(null)
    setLogsStatus('')
    setLogsReloadKey(Date.now())
    setIsLogsModalOpen(true)
  }

  const handleCloseLogs = () => {
    setIsLogsModalOpen(false)
    setSelectedDevice('')
    setDeviceChartData(null)
    setLogsStatus('')
  }

  useEffect(() => {
    if (!isLogsModalOpen || !selectedDevice) {
      return
    }

    let isCancelled = false

    const loadLogs = async () => {
      setLogsLoading(true)
      setLogsStatus(t('dashboard.loadingData'))

      try {
        const series = await fetchDeviceSeries(selectedDevice, logsTimeRange)
        if (isCancelled) return

        const chartData = buildDeviceChartData(series, t)
        setDeviceChartData(chartData)
        setLogsStatus(chartData ? '' : t('common.noDataToDisplay'))
      } catch (error) {
        if (!isCancelled) {
          setDeviceChartData(null)
          setLogsStatus(t('dashboard.errorLoadingDevice'))
        }
      } finally {
        if (!isCancelled) {
          setLogsLoading(false)
        }
      }
    }

    loadLogs()

    return () => {
      isCancelled = true
    }
  }, [fetchDeviceSeries, isLogsModalOpen, logsReloadKey, logsTimeRange, selectedDevice])

  const handleOpenSpeedtest = (device) => {
    setSelectedSpeedtestDevice(device)
  }

  const handleCloseSpeedtest = () => {
    setSelectedSpeedtestDevice(null)
  }

  // Получаем имя устройства для SpeedtestModal
  const selectedDeviceName = selectedSpeedtestDevice
    ? (selectedSpeedtestDevice.school || selectedSpeedtestDevice.name || getDeviceIdentifier(selectedSpeedtestDevice) || t('common.unknownDevice'))
    : ''

  // Получаем имя устройства для HistoryGraphModal по IP
  const selectedDeviceForLogs = devices.find(device => 
    getDeviceIdentifier(device) === selectedDevice
  )
  const logsDeviceName = selectedDeviceForLogs
    ? (selectedDeviceForLogs.school || selectedDeviceForLogs.name || selectedDevice || t('common.unknownDevice'))
    : selectedDevice || t('common.unknownDevice')

  const handleOpenDetails = async (device) => {
    const ipAddress = getDeviceIdentifier(device)
    if (!ipAddress) {
      return
    }

    try {
      const overviewData = await fetchSchoolOverview(ipAddress)
      navigate(`/schools/${encodeURIComponent(ipAddress)}`, {
        state: {
          ip: ipAddress,
          school: overviewData?.school || device.school,
          overview: overviewData,
        },
      })
    } catch (error) {
      navigate(`/schools/${encodeURIComponent(ipAddress)}`, {
        state: {
          ip: ipAddress,
          school: device.school,
        },
      })
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="absolute inset-0 -z-10 bg-brand-radial opacity-50" />
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={toggleSidebar}
        onLogout={handleLogout}
        userEmail={userEmail}
        userRole={userRole || role}
      />
      <div className="flex flex-1">
        <Sidebar
          onLogout={handleLogout}
          userRole={userRole || role}
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />
        <main className="relative flex min-w-0 flex-1 flex-col gap-4 bg-surface-base/60 px-3 py-4 transition-all duration-300 ease-brand sm:gap-6 sm:px-4 sm:py-6 lg:px-10">
          <RegionsStats 
            data={regionsStats} 
            isLoading={summaryLoading}
            onRegionClick={handleRegionClick}
          />

          <div id="devices-table">
            <DevicesTable
              devices={devices}
              onLogsClick={handleOpenLogs}
              onSpeedtestClick={handleOpenSpeedtest}
              onDetailsClick={handleOpenDetails}
              externalRegionFilter={selectedRegion}
              className="mb-6"
            />
          </div>
        </main>
      </div>

      <HistoryGraphModal
        isOpen={isLogsModalOpen}
        onClose={handleCloseLogs}
        deviceName={logsDeviceName}
        deviceIP={selectedDevice}
        logsContent={logsStatus}
        timeRange={logsTimeRange}
        onTimeRangeChange={setLogsTimeRange}
        chartData={deviceChartData}
        isLoading={logsLoading}
      />

      <SpeedtestModalWebSocket
        isOpen={Boolean(selectedSpeedtestDevice)}
        onClose={handleCloseSpeedtest}
        deviceName={selectedDeviceName}
        deviceIP={getDeviceIdentifier(selectedSpeedtestDevice) || ''}
      />
    </div>
  )
}

export default RegionsStatsPage

